import type { FaceLandmarker } from '@mediapipe/tasks-vision';
import wasmLoaderPath from '@mediapipe/tasks-vision/vision_wasm_internal.js?url';
import wasmBinaryPath from '@mediapipe/tasks-vision/vision_wasm_internal.wasm?url';

/**
 * MediaPipe Face Landmarker, run on the guest's phone: finds the 478 points of
 * a face in the selfie. Everything (Wasm runtime, model) is served by KaroZik
 * itself, nothing is sent anywhere. Loaded lazily, when the guest taps the
 * selfie button, so it warms up while the camera is open (≈ 15 MB, then cached
 * by the browser).
 */
let landmarker: Promise<FaceLandmarker | null> | null = null;

export function loadLandmarker(): Promise<FaceLandmarker | null> {
  landmarker ??= (async () => {
    const { FaceLandmarker } = await import('@mediapipe/tasks-vision');
    const create = (delegate: 'GPU' | 'CPU') =>
      FaceLandmarker.createFromOptions(
        { wasmLoaderPath, wasmBinaryPath },
        { baseOptions: { modelAssetPath: '/models/face_landmarker.task', delegate }, runningMode: 'IMAGE', numFaces: 1 },
      );
    try {
      return await create('GPU');
    } catch {
      return await create('CPU');
    }
  })().catch((e) => {
    console.warn('Face landmarker unavailable', e);
    return null;
  });
  return landmarker;
}

export interface Point {
  x: number;
  y: number;
}

/** The face's landmarks in the image's pixels, or null if no face was found (or the detector could not load). */
export async function detectFace(img: HTMLCanvasElement): Promise<Point[] | null> {
  const lm = await loadLandmarker();
  if (!lm) return null;
  try {
    const face = lm.detect(img).faceLandmarks[0];
    if (!face) return null;
    return face.map((p) => ({ x: p.x * img.width, y: p.y * img.height }));
  } catch (e) {
    console.warn('Face detection failed', e);
    return null;
  }
}

/** Eye centres (image left, image right), from the eye corners: steadier than the irises, which follow the gaze. */
export function eyeCentres(pts: Point[]): [Point, Point] {
  const mid = (i: number, j: number): Point => ({ x: (pts[i]!.x + pts[j]!.x) / 2, y: (pts[i]!.y + pts[j]!.y) / 2 });
  return [mid(33, 133), mid(362, 263)];
}
